/* eslint-disable import/no-restricted-paths */
import { ComponentProps, useContext } from 'react';
import { Module } from '../../../../../api/module';
import { useTranslation } from 'react-i18next';
import { WordlistContextProvider } from '../../../../../modules/material/_wordlist/_contexts/WordlistContext';
import { WordlistMedialink } from '../../../../../api/medialink';
import classNames from 'classnames';
import MaterialDialogHeader from '../../../components/MaterialDialogHeader';
import ModalContext from '../../../../../contexts/ModalContext';
import WordlistFocusMode from '../../../../../modules/material/_wordlist/WordlistFocusMode';
import WordlistListMode from '../../../../../modules/material/_wordlist/WordlistListMode';
import WordlistViewModeContext, {
  WordlistViewMode,
  WordlistViewModeContextProvider,
} from '../../../../../modules/material/_wordlist/_contexts/WordlistViewModeContext';

import Dialog from '@pelckmans/elementis/components/dialog';

type Base = {
  id: string;
  wordlistId: string;
  module: Module;
  headerProps?: Pick<ComponentProps<typeof MaterialDialogHeader>, 'title' | 'tags' | 'titleTooltip'>;
};

type StandAloneWordlistProps = Base & {
  standalone: true;
  name?: never;
  medialink?: never;
};

type WordlistAppProps = Base & {
  name: string;
  standalone?: never;
  medialink?: never;
};

type WordlistProps = Base & {
  name: string;
  medialink: WordlistMedialink;
  standalone?: never;
};

type Props = StandAloneWordlistProps | WordlistAppProps | WordlistProps;

export default function WordlistModal(props: Props) {
  const { id, wordlistId, module, headerProps } = props;
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  const size: ComponentProps<typeof Dialog>['size'] = 'full';

  return (
    <Dialog
      id={id}
      modal
      className={classNames('material-dialog', 'wordlist', {
        'wordlist--standalone': props.standalone,
      })}
      onClose={() => closeModal(id)}
      size={size}
      aria-label={t('SHARED_COMPONENTS.WORD_LIST_MODAL.A11Y_LABEL')}
    >
      <WordlistContextProvider wordlistId={wordlistId} module={module}>
        <WordlistViewModeContextProvider>
          <WordlistViewModeContext.Consumer>
            {({ viewMode }) => (
              <>
                {headerProps && (
                  <MaterialDialogHeader
                    {...headerProps}
                    onClose={() => closeModal(id)}
                    showCloseLabelAsText={size === 'full'}
                  />
                )}
                <Dialog.Body className="wordlist__body">
                  {viewMode === WordlistViewMode.LIST && <WordlistListMode />}
                  {viewMode === WordlistViewMode.FOCUS && <WordlistFocusMode />}
                </Dialog.Body>
              </>
            )}
          </WordlistViewModeContext.Consumer>
        </WordlistViewModeContextProvider>
      </WordlistContextProvider>
    </Dialog>
  );
}
